import React, {useState} from "react";
import {Box, Text, Button, Tiles, View} from "@unibuddy/patron";
import {IconCheck, IconXMark} from "@unibuddy/icons";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {QuestionsPromptPopUpTopicBubble} from "./QuestionsPromptPopUpButton";
import {QuestionsPromptPopUpQuestion} from "./QuestionsPromptPopUpQuestionButton";
import {
    questionsPromptPopUpTopics,
    questionPromptTopicObject,
    dismissText,
    closeText,
} from "./QuestionsPromptPopUpConstants";

type QuestionsPromptPopUpProps = {
    onClose: () => void;
    onQuestionSelect: (question: string) => void;
};

export function QuestionsPromptPopUp({onClose, onQuestionSelect}: QuestionsPromptPopUpProps) {
    const [selectedTopic, setSelectedTopic] = useState<questionsPromptPopUpTopics | null>(
        questionsPromptPopUpTopics.StudentLife,
    );
    const {trackEvent} = useAnalytics();

    const handleTopicSelect = (currentTopic: questionsPromptPopUpTopics) => {
        trackEvent(sharedEvents.questionsPopupAction.name, {
            cta: currentTopic,
            type: "topic",
            page: pageNames.dummyChat,
            position: Object.values(questionsPromptPopUpTopics).indexOf(currentTopic) + 1,
        });
        setSelectedTopic(currentTopic);
    };

    const handleQuestionSelect = (question: string) => {
        trackEvent(sharedEvents.questionsPopupAction.name, {
            cta: question,
            type: "question",
            page: pageNames.dummyChat,
            position: questionPromptTopicObject[selectedTopic].indexOf(question) + 1,
        });
        onQuestionSelect(question);
        onClose();
    };

    const handleOnDismiss = () => {
        trackEvent(sharedEvents.questionsPopupActionClosed.name, {
            cta: closeText,
            page: pageNames.dummyChat,
            type: "dismiss",
        });
        onClose();
    };

    const handleCustomQuestionDismissal = () => {
        trackEvent(sharedEvents.questionsPopupActionClosed.name, {
            cta: dismissText,
            page: pageNames.dummyChat,
            type: "custom_question",
        });
        onClose();
    };

    return (
        <Box display="flex" alignItems="center" flexDirection="column" bgColor="white">
            <Box borderBottomWidth={1} width="100%" borderColor="grey150" />
            <Box
                display="flex"
                alignItems="flex-start"
                flexDirection="column"
                gap="xxsmall"
                padding="small-medium"
                width="100%"
            >
                <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                    <Text size="large" weight="bold">
                        Not sure what to ask?
                    </Text>
                    <Button
                        iconOnly
                        clear
                        round
                        size="sm"
                        onClick={handleOnDismiss}
                        aria-label={closeText}
                    >
                        <IconXMark />
                    </Button>
                </Box>

                <Text>Here are some common questions our ambassadors answer.</Text>

                <Box
                    display="flex"
                    flexDirection="row"
                    gap="small"
                    flexWrap="wrap"
                    paddingTop="small-medium"
                >
                    {Object.values(questionsPromptPopUpTopics).map((topic) => {
                        return (
                            <QuestionsPromptPopUpTopicBubble
                                key={topic}
                                topic={topic}
                                selected={topic === selectedTopic}
                                onClick={() => handleTopicSelect(topic)}
                            />
                        );
                    })}
                </Box>
            </Box>

            <Box borderBottomWidth={1} width="100%" borderColor="grey150" />

            <View alignItems="center" gap="small-medium" padding="small-medium">
                {selectedTopic && (
                    <Tiles columns={2} space="xsmall">
                        {questionPromptTopicObject[selectedTopic].map((question) => (
                            <QuestionsPromptPopUpQuestion
                                key={question}
                                question={question}
                                onQuestionSelect={handleQuestionSelect}
                            />
                        ))}
                    </Tiles>
                )}

                <Button block onClick={handleCustomQuestionDismissal}>
                    <IconCheck />
                    {dismissText}
                </Button>
            </View>
        </Box>
    );
}
